import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { AutoComplete } from 'primereact/autocomplete';
import axiosInstance from '../config/axios.config';
import leadSharingSettingsService from '../services/leadSharingSettingsService';

const USERS_PER_PAGE = 20;

const ShareLeadDialog = ({ visible, onHide, lead, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [tenantUsers, setTenantUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [restrictionMessage, setRestrictionMessage] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [currentUserType, setCurrentUserType] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [shareData, setShareData] = useState({
        share_with: null,
        share_type: 'view',
        message: '',
        include_reports: true,
        expires_at: null
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (visible) {
            // Reset all states when dialog opens
            setShareData({
                share_with: null,
                share_type: 'view',
                message: '',
                include_reports: true,
                expires_at: null
            });
            setErrors({});
            setRestrictionMessage(null);
            setSelectedCategory(null);
            setAvailableCategories([]);
            setTenantUsers([]);
            setFilteredUsers([]);
            setCurrentPage(1);
            setHasMore(false);
            setTotalUsers(0);
            setSearchQuery('');

            // Load initial users
            loadShareableUsers(null, null, 1, true);
        }
    }, [visible]);

    const loadShareableUsers = async (search = null, category = null, page = 1, isInitialLoad = false) => {
        if (isInitialLoad) {
            setLoadingUsers(true);
        } else {
            setLoadingMore(true);
        }

        try {
            console.log('Fetching shareable users...', { search, category, page });
            const params = {
                page,
                limit: USERS_PER_PAGE
            };
            if (search) {
                params.search = search;
            }
            if (category) {
                params.category = category;
            }

            const response = await leadSharingSettingsService.getShareableUsers(params);
            console.log('Shareable users response:', response);

            if (response.success) {
                const data = response.data;

                // Store current user type for category options
                if (data.current_user_type) {
                    setCurrentUserType(data.current_user_type);
                }

                // Handle flat users array format
                if (data.users && Array.isArray(data.users)) {
                    const mappedUsers = data.users.map(user => ({
                        label: `${user.name} (${user.email})`,
                        value: user.email,
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        category: user.share_category || 'User',
                        companyName: user.company_name
                    }));

                    if (isInitialLoad || page === 1) {
                        // IMPORTANT: Set both tenantUsers AND filteredUsers
                        // filteredUsers is what the AutoComplete dropdown shows
                        setTenantUsers(mappedUsers);
                        setFilteredUsers(mappedUsers);

                        console.log(`Loaded ${mappedUsers.length} users with category filter:`, category);

                        // Extract unique categories for filter dropdown (only on true initial load, no filters)
                        if (isInitialLoad && !search && !category) {
                            // Map display names to backend category codes
                            const categoryDisplayToCode = {
                                'Company Admin': 'company_admins',
                                'Employee': 'employees',
                                'General User': 'general_users'
                            };

                            const uniqueDisplayCategories = [...new Set(mappedUsers.map(u => u.category))];
                            const categoryOptions = [
                                { label: 'All Categories', value: null }
                            ];

                            // Add categories with proper backend codes
                            uniqueDisplayCategories.forEach(displayName => {
                                if (categoryDisplayToCode[displayName]) {
                                    categoryOptions.push({
                                        label: displayName,
                                        value: categoryDisplayToCode[displayName]
                                    });
                                }
                            });

                            setAvailableCategories(categoryOptions);
                        }
                    } else {
                        // Append to existing users for "Load More"
                        setTenantUsers(prev => [...prev, ...mappedUsers]);
                        setFilteredUsers(prev => [...prev, ...mappedUsers]);
                    }

                    // Update pagination state
                    setTotalUsers(data.total || mappedUsers.length);
                    setHasMore(data.has_more || false);
                    setCurrentPage(page);

                    // Check for restriction message
                    if (data.restriction_message) {
                        setRestrictionMessage(data.restriction_message);
                    }
                } else {
                    if (isInitialLoad) {
                        setTenantUsers([]);
                        setFilteredUsers([]);
                        setAvailableCategories([]);
                    }
                    setHasMore(false);
                }
            } else {
                console.error('Failed to load users:', response.message);
            }
        } catch (error) {
            console.error('Error loading shareable users:', error);
            console.error('Error details:', error.response?.data);
        } finally {
            setLoadingUsers(false);
            setLoadingMore(false);
        }
    };

    // Load more users
    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            loadShareableUsers(searchQuery, selectedCategory, currentPage + 1, false);
        }
    };

    const searchUsers = async (event) => {
        const query = event.query ? event.query.trim() : '';
        setSearchQuery(query);

        // If no search query, use already loaded users (category filter already applied by backend)
        if (!query) {
            setFilteredUsers(tenantUsers);
            return;
        }

        // Search from backend with query and category filter
        try {
            const params = {
                page: 1,
                limit: USERS_PER_PAGE,
                search: query
            };
            if (selectedCategory) {
                params.category = selectedCategory;
            }

            const response = await leadSharingSettingsService.getShareableUsers(params);

            if (response.success && response.data.users) {
                const searchedUsers = response.data.users.map(user => ({
                    label: `${user.name} (${user.email})`,
                    value: user.email,
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    category: user.share_category || 'User',
                    companyName: user.company_name
                }));
                setFilteredUsers(searchedUsers);
                setHasMore(response.data.has_more || false);
                setTotalUsers(response.data.total || searchedUsers.length);
                setCurrentPage(1);
            } else {
                setFilteredUsers([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error searching users:', error);
            // Fallback to local filtering
            const filtered = tenantUsers.filter(user =>
                user.label.toLowerCase().includes(query.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    };

    // Handle category filter change - fetch from backend
    const handleCategoryChange = async (e) => {
        const category = e.value;
        console.log('Category changed to:', category);
        setSelectedCategory(category);
        setSearchQuery('');

        // Clear the selected user when category changes
        setShareData(prev => ({ ...prev, share_with: null }));

        // Fetch from backend with new category filter
        console.log('Calling loadShareableUsers with category:', category);
        await loadShareableUsers(null, category, 1, true);

        // Force AutoComplete to show all filtered users by triggering completeMethod
        // This simulates user clicking the dropdown button
        setTimeout(() => {
            searchUsers({ query: '' });
        }, 100);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!shareData.share_with) {
            newErrors.share_with = 'Please select a user to share with';
        }

        if (shareData.expires_at && new Date(shareData.expires_at) <= new Date()) {
            newErrors.expires_at = 'Expiry date must be in the future';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleShare = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const sharePayload = {
                ...shareData,
                share_with: typeof shareData.share_with === 'object'
                    ? shareData.share_with.value
                    : shareData.share_with
            };

            const response = await axiosInstance.post(`/leads/${lead.id}/share`, sharePayload);

            if (response.data.success) {
                onSuccess({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Lead shared successfully',
                    life: 3000
                });
                onHide();
            }
        } catch (error) {
            console.error('Error sharing lead:', error);
            setErrors({
                general: error.response?.data?.message || 'Failed to share lead'
            });
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={loading}
            />
            <Button
                label="Share"
                icon="pi pi-share-alt"
                onClick={handleShare}
                loading={loading}
            />
        </div>
    );

    return (
        <Dialog
            header={`Share Lead: ${lead?.full_name || ''}`}
            visible={visible}
            style={{ width: '500px' }}
            footer={footer}
            onHide={onHide}
            modal
            className="p-fluid"
        >
            {errors.general && (
                <Message severity="error" text={errors.general} className="mb-3" />
            )}

            <div className="field">
                <label htmlFor="share_with" className="block mb-2">
                    Share With <span className="text-red-500">*</span>
                </label>
                {loadingUsers ? (
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-spinner pi-spin"></i>
                        <span>Loading users...</span>
                    </div>
                ) : tenantUsers.length === 0 && !restrictionMessage ? (
                    <Message
                        severity="info"
                        text="No other users found in your tenant. Please ask your administrator to add more users."
                        className="w-full"
                    />
                ) : tenantUsers.length === 0 && restrictionMessage ? (
                    <Message
                        severity="warn"
                        text={restrictionMessage}
                        className="w-full"
                    />
                ) : (
                    <>
                        {restrictionMessage && (
                            <Message
                                severity="warn"
                                text={restrictionMessage}
                                className="w-full mb-2"
                            />
                        )}
                        {/* Category Filter */}
                        {availableCategories.length > 2 && (
                            <div className="mb-2">
                                <Dropdown
                                    value={selectedCategory}
                                    options={availableCategories}
                                    onChange={handleCategoryChange}
                                    placeholder="Filter by category"
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        )}
                        <AutoComplete
                            id="share_with"
                            value={shareData.share_with}
                            suggestions={filteredUsers}
                            completeMethod={searchUsers}
                            onChange={(e) => setShareData({...shareData, share_with: e.value})}
                            field="label"
                            dropdown
                            placeholder="Search and select user (type to search)"
                            className={errors.share_with ? 'p-invalid w-full' : 'w-full'}
                            forceSelection
                            emptyMessage="No users found"
                            itemTemplate={(item) => (
                                <div className="flex align-items-center justify-content-between w-full">
                                    <div>
                                        <div className="font-semibold">{item.name}</div>
                                        <div className="text-sm text-gray-500">
                                            {item.email}
                                            {item.companyName && <span className="ml-2">({item.companyName})</span>}
                                        </div>
                                    </div>
                                    {item.category && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 border-round">
                                            {item.category}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                        {errors.share_with && <small className="p-error">{errors.share_with}</small>}

                        {/* User count and Load More */}
                        <div className="flex justify-content-between align-items-center mt-2">
                            <small className="text-gray-500">
                                {searchQuery || selectedCategory
                                    ? `Showing ${filteredUsers.length} of ${totalUsers} user${totalUsers !== 1 ? 's' : ''}`
                                    : `${totalUsers} user${totalUsers !== 1 ? 's' : ''} available`
                                }
                            </small>
                            {hasMore && (
                                <Button
                                    label={loadingMore ? 'Loading...' : 'Load More'}
                                    icon={loadingMore ? 'pi pi-spinner pi-spin' : 'pi pi-plus'}
                                    className="p-button-text p-button-sm"
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="field">
                <label className="block mb-2">Access Level</label>
                <div className="flex flex-column gap-2">
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="view"
                            name="share_type"
                            value="view"
                            onChange={(e) => setShareData({...shareData, share_type: e.value})}
                            checked={shareData.share_type === 'view'}
                        />
                        <label htmlFor="view" className="ml-2">
                            <strong>View Only</strong> - Can view lead details and reports
                        </label>
                    </div>
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="edit"
                            name="share_type"
                            value="edit"
                            onChange={(e) => setShareData({...shareData, share_type: e.value})}
                            checked={shareData.share_type === 'edit'}
                        />
                        <label htmlFor="edit" className="ml-2">
                            <strong>Edit</strong> - Can view and edit lead details
                        </label>
                    </div>
                    <div className="flex align-items-center">
                        <RadioButton
                            inputId="full"
                            name="share_type"
                            value="full"
                            onChange={(e) => setShareData({...shareData, share_type: e.value})}
                            checked={shareData.share_type === 'full'}
                        />
                        <label htmlFor="full" className="ml-2">
                            <strong>Full Access</strong> - Can view, edit, and run reports
                        </label>
                    </div>
                </div>
            </div>

            <div className="field">
                <div className="flex align-items-center mb-2">
                    <Checkbox
                        inputId="include_reports"
                        checked={shareData.include_reports}
                        onChange={(e) => setShareData({...shareData, include_reports: e.checked})}
                    />
                    <label htmlFor="include_reports" className="ml-2">
                        Include Associated Reports
                    </label>
                </div>
                <small className="text-500">
                    Share all API reports (Equifax, PAN, Bank Statement, etc.) associated with this lead
                </small>
            </div>

            <div className="field">
                <label htmlFor="message" className="block mb-2">Message (Optional)</label>
                <InputTextarea
                    id="message"
                    value={shareData.message}
                    onChange={(e) => setShareData({...shareData, message: e.target.value})}
                    rows={3}
                    placeholder="Add a note for the recipient..."
                    maxLength={500}
                />
                <small className="text-500">{shareData.message.length}/500 characters</small>
            </div>

            <div className="field">
                <label htmlFor="expires_at" className="block mb-2">Expiry Date (Optional)</label>
                <Calendar
                    id="expires_at"
                    value={shareData.expires_at}
                    onChange={(e) => setShareData({...shareData, expires_at: e.value})}
                    showIcon
                    minDate={new Date()}
                    dateFormat="dd/mm/yy"
                    placeholder="Never expires"
                    className={errors.expires_at ? 'p-invalid' : ''}
                />
                {errors.expires_at && <small className="p-error">{errors.expires_at}</small>}
                <small className="text-500 block mt-1">
                    Leave empty for no expiration
                </small>
            </div>

            {/* Summary */}
            <div className="surface-100 border-round p-3 mt-3">
                <h5 className="mt-0 mb-2">Share Summary</h5>
                <ul className="m-0 pl-3">
                    <li>Lead: <strong>{lead?.full_name}</strong></li>
                    <li>Access: <strong>{shareData.share_type === 'view' ? 'View Only' : shareData.share_type === 'edit' ? 'Edit' : 'Full Access'}</strong></li>
                    <li>Reports: <strong>{shareData.include_reports ? 'Included' : 'Not Included'}</strong></li>
                    <li>Expires: <strong>{shareData.expires_at ? new Date(shareData.expires_at).toLocaleDateString() : 'Never'}</strong></li>
                </ul>
            </div>
        </Dialog>
    );
};

export default ShareLeadDialog;
